/**
 * validate.js — input validation.
 *
 * v1 had no format validation anywhere: GSTIN, PAN, email, pincode and phone
 * all had dedicated inputs and accepted literally any string. Validation that
 * did exist was an if/else-if chain surfacing one error at a time via a
 * transient toast, with no inline field errors and no focus management.
 *
 * Everything here returns either null (valid) or a human-readable message, so
 * callers can build a `{ field: message }` map in one pass.
 */

/* ------------------------------------------------------------------ *
 * GSTIN
 * ------------------------------------------------------------------ */

const GSTIN_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const GSTIN_RE = /^[0-3][0-9][A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/**
 * Verify the GSTIN check digit (the 15th character).
 * Algorithm: each of the first 14 characters is converted to its index in
 * 0-9A-Z, multiplied by 1 or 2 alternating, the product is reduced by
 * quotient+remainder of division by 36, summed, and the check digit is
 * (36 - sum % 36) % 36.
 */
export function isGstinChecksumValid(gstin) {
  const g = String(gstin || '').toUpperCase();
  if (g.length !== 15) return false;

  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const code = GSTIN_CHARS.indexOf(g[i]);
    if (code < 0) return false;
    const factor = i % 2 === 0 ? 1 : 2;
    const product = code * factor;
    sum += Math.floor(product / 36) + (product % 36);
  }
  const checkCode = (36 - (sum % 36)) % 36;
  return GSTIN_CHARS[checkCode] === g[14];
}

export function validateGstin(value, { required = false } = {}) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return required ? 'GSTIN is required.' : null;
  if (v.length !== 15) return 'GSTIN must be exactly 15 characters.';
  if (!GSTIN_RE.test(v)) return 'That is not a valid GSTIN format.';
  if (!isGstinChecksumValid(v)) return 'GSTIN check digit is wrong — please re-check the number.';
  return null;
}

/* ------------------------------------------------------------------ *
 * PAN, phone, email, pincode, IFSC
 * ------------------------------------------------------------------ */

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function validatePan(value, { required = false } = {}) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return required ? 'PAN is required.' : null;
  if (!PAN_RE.test(v)) return 'PAN must look like ABCDE1234F.';
  return null;
}

/**
 * The PAN inside a GSTIN.
 *
 * A GSTIN is not an opaque identifier. It is built to a fixed layout:
 *
 *     2 7  A A P F U 0 9 3 9 F  1  Z  V
 *     └┬┘  └──────────┬──────┘  │  │  │
 *      │              │         │  │  └─ check digit
 *      │              │         │  └──── always 'Z', reserved
 *      │              │         └─────── entity number for this PAN in
 *      │              │                  this state
 *      │              └───────────────── the holder's PAN, characters 3-12
 *      └──────────────────────────────── state code
 *
 * So a registered party's PAN never has to be typed, asked for, or fetched
 * over an API — it is already inside the GSTIN, and reading it out is exact
 * rather than a guess. Same reasoning as `stateCodeFromGstin`, which is why
 * the state select is driven from the GSTIN too.
 *
 * Returns '' unless the GSTIN passes format AND checksum: a half-typed number
 * would otherwise yield a plausible-looking PAN that is wrong, and a wrong PAN
 * on a filed return is worse than a blank one.
 *
 * @param {string} gstin
 * @return {string} a 10-character PAN, or '' when one cannot be read out
 */
export function panFromGstin(gstin) {
  const g = String(gstin || '').trim().toUpperCase();
  if (validateGstin(g, { required: true })) return '';

  const pan = g.slice(2, 12);
  // The GSTIN pattern is looser than the PAN pattern in one position, so the
  // extracted value is checked against the PAN rules rather than trusted
  // because of where it came from.
  return PAN_RE.test(pan) ? pan : '';
}

/**
 * Refuse a PAN that contradicts the GSTIN beside it.
 *
 * Both fields are filled from the GSTIN now, so this can only fire on a record
 * created before that, or one edited by hand. Either way the two cannot both
 * be right, and letting them disagree puts one wrong number on a return.
 */
export function validatePanAgainstGstin(pan, gstin) {
  const derived = panFromGstin(gstin);
  const value = String(pan || '').trim().toUpperCase();
  if (!derived || !value || derived === value) return null;
  return `This GSTIN belongs to PAN ${derived}, which is not the PAN entered.`;
}

/** Indian mobile numbers: 10 digits starting 6-9. */
export function validatePhone(value, { required = true } = {}) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return required ? 'Phone number is required.' : null;
  const local = digits.length > 10 ? digits.slice(-10) : digits;
  if (local.length !== 10) return 'Enter a 10-digit mobile number.';
  if (!/^[6-9]/.test(local)) return 'Indian mobile numbers start with 6, 7, 8 or 9.';
  return null;
}

export function normalisePhone(value, countryCode = '+91') {
  const digits = String(value || '').replace(/\D/g, '');
  const local = digits.length > 10 ? digits.slice(-10) : digits;
  return `${countryCode}${local}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateEmail(value, { required = false } = {}) {
  const v = String(value || '').trim();
  if (!v) return required ? 'Email is required.' : null;
  if (!EMAIL_RE.test(v)) return 'That does not look like a valid email address.';
  return null;
}

export function validatePincode(value, { required = false } = {}) {
  const v = String(value || '').trim();
  if (!v) return required ? 'PIN code is required.' : null;
  if (!/^[1-9][0-9]{5}$/.test(v)) return 'PIN code must be 6 digits and cannot start with 0.';
  return null;
}

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export function validateIfsc(value, { required = false } = {}) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return required ? 'IFSC is required.' : null;
  if (!IFSC_RE.test(v)) return 'IFSC must look like HDFC0001234.';
  return null;
}

/** HSN (goods) is 4/6/8 digits; SAC (services) is 6 digits. */
export function validateHsn(value, { required = false } = {}) {
  const v = String(value || '').trim();
  if (!v) return required ? 'HSN/SAC is required.' : null;
  if (!/^\d{4}(\d{2})?(\d{2})?$/.test(v)) return 'HSN must be 4, 6 or 8 digits (SAC is 6).';
  return null;
}

/* ------------------------------------------------------------------ *
 * Generic helpers
 * ------------------------------------------------------------------ */

export function validateRequired(value, label = 'This field') {
  if (value === null || value === undefined) return `${label} is required.`;
  if (typeof value === 'string' && value.trim() === '') return `${label} is required.`;
  if (Array.isArray(value) && value.length === 0) return `${label} is required.`;
  return null;
}

/**
 * Validate a numeric input. v1's adjust-stock dialog was type="text" and only
 * rejected empty and exactly-zero, so "abc" was stored and turned an item's
 * entire stock into NaN for every user.
 */
export function validateNumber(
  value,
  { label = 'Value', required = true, min = null, max = null, allowZero = true, integer = false } = {}
) {
  if (value === '' || value === null || value === undefined) {
    return required ? `${label} is required.` : null;
  }
  const n = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return `${label} must be a number.`;
  if (!allowZero && n === 0) return `${label} cannot be zero.`;
  if (integer && !Number.isInteger(n)) return `${label} must be a whole number.`;
  if (min !== null && n < min) return `${label} cannot be less than ${min}.`;
  if (max !== null && n > max) return `${label} cannot be more than ${max}.`;
  return null;
}

/**
 * Run a field->validator map over a values object and return the errors.
 * Collects EVERY error, not just the first — v1 showed one at a time.
 */
export function runValidators(values, validators) {
  const errors = {};
  Object.entries(validators).forEach(([field, validator]) => {
    const message = validator(values[field], values);
    if (message) errors[field] = message;
  });
  return errors;
}

export const hasErrors = (errors) => Object.keys(errors || {}).length > 0;
